import React, { LabelHTMLAttributes } from "react"

import styled, { css } from "styled-components"

import { colors, spacing, spacingNumeric, sizeNumeric, typography } from "@izettle/otto"

import { SemanticState, InputSize } from "../../enumerations"

export type FloatingLabelProps = {
  children?: any // The content of a label
  isElevated?: boolean // Is the label is elevated, minified and transformed into a label
  isFocused?: boolean // Is the input is focused upon
  isDisabled?: boolean // Is the input is disabled
  isWithIcon?: boolean // Determines whether the unelevated placholder is preceded by an icon
  size?: InputSize // Height of the input component
  semanticState?: SemanticState // Determines the semantic state of the color palette
  isHidden?: boolean // If the label is going to be rendered
  isErrorStateOverriden?: boolean
} & LabelHTMLAttributes<HTMLLabelElement>

const mixinFloatingLabelTextIcon = css<FloatingLabelProps>`
  transform: translateX(${spacingNumeric.xxSmall * 2 + spacingNumeric.medium}px);
`

const mixinFloatingLabelPalette = css<FloatingLabelProps>`
  color: ${({
    semanticState,
    isErrorStateOverriden,
    isDisabled,
    isFocused,
    isElevated,
  }) => {
    if (isErrorStateOverriden) {
      return isElevated ? colors.icon.error.large : colors.icon.error.small
    }
    if (isDisabled) {
      return colors.text.disabled
    }
    if (isFocused) {
      return colors.action.secondary.text.default
    }
    return {
      [SemanticState.Warning]: isElevated
        ? colors.icon.warning.large
        : colors.icon.warning.small,
      [SemanticState.Error]: isElevated ? colors.text.error : colors.icon.error.small,
      [SemanticState.Success]: isElevated ? colors.text.success : colors.text.success,
      [SemanticState.Default]: colors.text.secondary,
    }[semanticState ?? SemanticState.Default]
  }};
`

const mixinFloatingLabelTextElevation = css<FloatingLabelProps>`
  transform: translateY(
      -${({ isElevated, size }) => {
          if (!isElevated) {
            return
          }
          return {
            [InputSize.Small]: sizeNumeric.small / 2,
            [InputSize.Medium]: sizeNumeric.xMedium / 2,
            [InputSize.Large]: sizeNumeric.large / 2,
          }[size ?? InputSize.Large]
        }}px
    )
    scale(0.75);
`

const FloatingLabelText = React.memo(styled.label<FloatingLabelProps>`
  ${typography.regular}
  box-sizing: border-box;
  pointer-events: none;
  position: absolute;
  margin-left: ${spacing.xxSmall};
  margin-bottom: 0;
  align-self: center;
  padding: 0 ${spacing.xxSmall};
  transition: transform 0.1s, color 0.1s;
  transform-origin: 0% 50%;
  z-index: 1;
  width: 100%;
  max-width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  transition-timing-function: cubic-bezier(0.25, 0.1, 0.25, 1);
  ${({ isWithIcon }) => isWithIcon && mixinFloatingLabelTextIcon}
  ${mixinFloatingLabelTextElevation}
  ${mixinFloatingLabelPalette}
  ${({ isHidden }) => isHidden && `opacity: 0;`}
`)

const FloatingLabelBackground = React.memo(styled.div<{
  isHidden?: boolean
  isElevated?: boolean
}>`
  ${typography.regular}
  pointer-events: none;
  background-color: ${colors.background.default};
  /* stylelint-disable-next-line */
  color: transparent;
  position: absolute;
  padding: 0 ${spacing.xxSmall};
  top: -2px;
  height: 3px;
  margin-left: ${spacing.small};
  transform-origin: 0% 50%;
  transition: transform 0.1s, opacity 0.15s;
  transform: scaleX(0);
  opacity: 0;
  max-width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  transition-timing-function: cubic-bezier(0.25, 0.1, 0.25, 1);

  ${({ isElevated, isHidden }) =>
    isElevated &&
    !isHidden &&
    css`
      transform: scaleX(0.75) translateX(-10px);
      opacity: 1;
    `}
`)

export const FloatingLabel = ({
  id,
  children,
  isHidden,
  isElevated,
  isFocused,
  isDisabled,
  isWithIcon,
  size,
  htmlFor,
  semanticState,
  isErrorStateOverriden,
}: FloatingLabelProps) => (
  <>
    <FloatingLabelText
      id={id}
      isHidden={isHidden}
      isElevated={isElevated}
      isFocused={isFocused}
      isWithIcon={isWithIcon}
      isDisabled={isDisabled}
      size={size}
      htmlFor={htmlFor}
      semanticState={semanticState}
      isErrorStateOverriden={isErrorStateOverriden}
    >
      {children}
    </FloatingLabelText>
    <FloatingLabelBackground isHidden={isHidden} isElevated={isElevated} aria-hidden>
      {children}
    </FloatingLabelBackground>
  </>
)
